import { Image, Upload, X } from 'lucide-react'
import { type ChangeEvent, useId, useRef, useState } from 'react'
import { useI18n } from '../../i18n'
import { usePlatform, usePlatformConfig } from '../../providers/platform-provider'
import type { CVPersonalInfo } from '../../types/cv'
import './editor.css'

export interface ProfilePhotoEditorProps {
  data: CVPersonalInfo
  onChange: (data: Partial<CVPersonalInfo>) => void
}

export function ProfilePhotoEditor({ data, onChange }: ProfilePhotoEditorProps) {
  const { t } = useI18n()
  const adapter = usePlatform()
  const config = usePlatformConfig()
  const inputId = useId()
  const errorId = `${inputId}-error`
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canUpload = config.features?.photoUpload ?? false
  const showPhoto = data.showPhoto ?? Boolean(data.photoUrl)

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError(t('imageUpload.invalidFile'))
      event.target.value = ''
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      const photoUrl = await adapter.storage.uploadPhoto(file)
      onChange({ photoUrl, showPhoto: true })
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : t('imageUpload.uploadFailed'))
    } finally {
      setIsUploading(false)
      event.target.value = ''
    }
  }

  return (
    <div className="profile-photo-editor">
      <div className="profile-photo-preview" data-empty={!data.photoUrl ? 'true' : undefined}>
        {data.photoUrl ? (
          <img
            src={data.photoUrl}
            alt={t('imageUpload.previewAlt', { label: t('fields.photo') })}
          />
        ) : (
          <Image aria-hidden="true" size={36} strokeWidth={1.6} />
        )}
      </div>

      <div className="profile-photo-controls">
        <label className="editor-checkbox profile-photo-toggle">
          <input
            type="checkbox"
            checked={showPhoto}
            disabled={!data.photoUrl}
            onChange={(event) => onChange({ showPhoto: event.target.checked })}
          />
          <span>{t('fields.useProfilePhoto')}</span>
        </label>

        <div className="profile-photo-actions">
          <button
            type="button"
            className="editor-btn editor-btn-secondary"
            disabled={!canUpload || isUploading}
            onClick={() => inputRef.current?.click()}
          >
            <Upload aria-hidden="true" size={16} strokeWidth={2} />
            <span>{isUploading ? t('imageUpload.uploading') : t('imageUpload.changePhoto')}</span>
          </button>
          {data.photoUrl && (
            <button
              type="button"
              className="editor-btn editor-btn-secondary profile-photo-remove"
              onClick={() => onChange({ photoUrl: '', showPhoto: false })}
            >
              <X aria-hidden="true" size={16} strokeWidth={2} />
              <span>{t('imageUpload.removePhoto')}</span>
            </button>
          )}
        </div>

        <p className="profile-photo-help">{t('editor.photo.uploadHelp')}</p>
        {!canUpload && <p className="profile-photo-help">{t('editor.photo.uploadDisabled')}</p>}
        {error && (
          <p id={errorId} className="profile-photo-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <input
        id={inputId}
        ref={inputRef}
        className="editor-sr-only"
        type="file"
        accept="image/*"
        onChange={handleUpload}
        aria-describedby={error ? errorId : undefined}
      />
    </div>
  )
}
